import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import API from '@/lib/api'

import { RaiffeisenBankCredentialsFormSchema } from './schema'

type RaiffeisenBankCredentialsFormValues = z.infer<
  typeof RaiffeisenBankCredentialsFormSchema
>

export const RaiffeisenBankCredentialsForm = ({
  onSuccess,
}: {
  onSuccess: () => void
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const methods = useForm<RaiffeisenBankCredentialsFormValues>({
    resolver: zodResolver(RaiffeisenBankCredentialsFormSchema),
    defaultValues: {
      certificate: '',
      password: '',
    },
  })
  const { handleSubmit } = methods
  const { t } = useTranslation(undefined, {
    keyPrefix: 'bank.raiffeisen',
  })

  const onValid = (values: RaiffeisenBankCredentialsFormValues) => {
    setIsSubmitting(true)
    API.post('/api/v2/raiffeisen-bank', values)
      .then(() => {
        toast.success(t('form.success.title'), {
          duration: 3000,
          closeButton: true,
        })
        methods.reset()
        onSuccess()
      })
      .catch(() => {
        toast.error(t('form.error.title'), {
          duration: 3000,
          closeButton: true,
        })
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onValid)}>
        <div className="space-y-4">
          <FormField
            name="certificate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.certificate.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="file"
                    accept=".p12,application/x-pkcs12"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (!file) return
                      const reader = new FileReader()
                      reader.onload = () => {
                        const result = reader.result as string
                        const base64 = result.includes(',')
                          ? result.split(',')[1]!
                          : result
                        field.onChange(base64)
                      }
                      reader.readAsDataURL(file)
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('form.password.label')}</FormLabel>
                <FormControl>
                  <Input {...field} type="password" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {t('form.submit.label')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

RaiffeisenBankCredentialsForm.displayName = 'RaiffeisenBankCredentialsForm'
